import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Scale, ShieldCheck, Users2, Building2, Landmark, GraduationCap, Cpu, Wallet,
  Vote, BadgeCheck, Trophy, ArrowRight, CheckCircle2, Info, Layers,
} from 'lucide-react';
import { CONSTITUTION } from '@/data/constitutionContent';

export default function ConstitutionPage() {
  const { t } = useLang();
  const C = CONSTITUTION;

  return (
    <div>
      {/* HERO */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <SectionKicker>NGRP Constitution</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] tracking-[-0.01em]">
            {C.title}
          </h1>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{C.subtitle}</p>

          {/* Supreme clause */}
          <Card className="mt-10 border-[#F47B20]/40 bg-[#062B5C] text-white" data-testid="constitution-supreme-clause">
            <CardContent className="p-6 sm:p-8">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                The most important constitutional clause
              </div>
              <p className="mt-3 font-display text-xl sm:text-2xl font-semibold leading-snug">{C.supreme_clause}</p>
              <p className="mt-4 text-sm text-white/85">
                No individual is above the institution. That single principle tells everyone that NGRP intends to build an institution — not a personality-centred organisation.
              </p>
            </CardContent>
          </Card>
        </Container>
      </section>

      {/* Foundational */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 1" title="Foundational philosophy" />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-6 border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Vision</div>
                <p className="mt-3 text-[#0B1220] leading-relaxed">{C.foundational.vision}</p>
                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Political philosophy</div>
                <p className="mt-2 font-display text-base font-semibold text-[#0B1220]">{C.foundational.philosophy}</p>
              </CardContent>
            </Card>
            <Card className="lg:col-span-6 border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">The leadership test</div>
                <p className="mt-3 font-display text-lg font-semibold text-[#0B1220]">{C.foundational.test}</p>
                <p className="mt-4 text-sm text-[#5B6B82] leading-relaxed">{C.foundational.reject}</p>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* 9-level architecture */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="Section 2"
            title="Organisational architecture — nine levels"
            subtitle="Each level has clearly delegated authority — nothing is controlled entirely from the national president."
          />
          <div className="mt-10 flex flex-col lg:flex-row items-stretch gap-2" data-testid="constitution-levels">
            {C.levels.map((lv, i) => (
              <React.Fragment key={lv}>
                <div className="flex-1 rounded-xl border border-[#D7E1EF] bg-white p-4 flex items-center gap-3">
                  <span className="h-7 w-7 rounded-md bg-[#062B5C] text-white text-xs font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-[#0B1220]">{lv}</span>
                </div>
                {i < C.levels.length - 1 && (
                  <div className="hidden lg:flex items-center text-[#062B5C]/40">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </section>

      {/* Village committee (Section 3) */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 3" title={C.village_committee.heading} subtitle={`${C.village_committee.size} · ${C.village_committee.purpose}`} />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-6 border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Roles at the local unit</div>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {C.village_committee.responsibilities.map((r) => (
                    <li key={r} className="flex items-center gap-2 text-sm text-[#0B1220]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#F47B20]" /> {r}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="lg:col-span-6 border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">NGRP Public Issues Register</div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {C.village_committee.register_fields.map((f, i) => (
                    <React.Fragment key={f}>
                      <span className="inline-flex items-center rounded-md border border-[#D7E1EF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#062B5C]">{f}</span>
                      {i < C.village_committee.register_fields.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Example issues</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {C.village_committee.issue_examples.map((e) => (
                    <span key={e} className="inline-flex items-center rounded-full bg-[#F5F8FC] text-[#0B1220] px-3 py-1.5 text-xs">{e}</span>
                  ))}
                </div>
                <p className="mt-5 text-sm text-[#062B5C] font-medium">{C.village_committee.outcome}</p>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* Cluster + Taluka (Section 4) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 4" title={C.cluster_taluka.heading} subtitle={C.cluster_taluka.body} />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-6">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Functions</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {C.cluster_taluka.functions.map((f) => (
                  <span key={f} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">{f}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Monthly report example</div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {C.cluster_taluka.monthly_report_example.map((r) => (
                  <div key={r.label} className="flex items-center justify-between rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] px-3 py-2">
                    <span className="text-xs text-[#5B6B82]">{r.label}</span>
                    <span className="kpi-num text-sm font-semibold text-[#0B1220]">{r.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm font-medium text-[#062B5C]">{C.cluster_taluka.tagline}</div>
            </div>
          </div>
        </Container>
      </section>

      {/* District Council (Section 5) */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 5" title={C.district_council.heading} subtitle={C.district_council.body} />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {C.district_council.functions.map((f) => (
              <div key={f} className="rounded-lg border border-[#D7E1EF] bg-white p-3 text-sm text-[#0B1220] flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#062B5C]" /> {f}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Assembly + Loksabha (Sections 6-7) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-6">
              <SectionKicker>Section 6</SectionKicker>
              <h3 className="font-display mt-3 text-xl sm:text-2xl font-semibold text-[#0B1220]">{C.assembly_constituency.heading}</h3>
              <p className="mt-3 text-[#5B6B82] leading-relaxed">{C.assembly_constituency.body}</p>
              <div className="mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Constituency database</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {C.assembly_constituency.database_areas.map((a) => (
                  <span key={a} className="inline-flex items-center rounded-full bg-[#F5F8FC] border border-[#D7E1EF] px-3 py-1.5 text-xs text-[#062B5C]">{a}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <SectionKicker>Section 7</SectionKicker>
              <h3 className="font-display mt-3 text-xl sm:text-2xl font-semibold text-[#0B1220]">{C.loksabha_council.heading}</h3>
              <p className="mt-3 text-[#5B6B82] leading-relaxed">{C.loksabha_council.body}</p>
              <blockquote className="mt-5 border-l-2 border-[#F47B20] pl-4 py-1 text-[#062B5C] font-medium">
                {C.loksabha_council.principle}
              </blockquote>
            </div>
          </div>
        </Container>
      </section>

      {/* State Council (Section 8) */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 8" title={C.state_council.heading} subtitle={C.state_council.intro} />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Executive team</div>
              <div className="mt-3 space-y-2">
                {C.state_council.roles.map((r) => (
                  <div key={r.title} className="rounded-lg border border-[#D7E1EF] bg-white p-3 flex items-center gap-3">
                    <BadgeCheck className="h-4 w-4 text-[#062B5C]" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#0B1220]">{r.title}</div>
                      {r.role && <div className="text-xs text-[#5B6B82]">{r.role}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Subject councils</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {C.state_council.subject_councils.map((s) => (
                  <div key={s} className="rounded-lg border border-[#D7E1EF] bg-[#F5F8FC] p-3 text-sm text-[#0B1220] flex items-center gap-2">
                    <Layers className="h-3.5 w-3.5 text-[#138A4B]" /> {s}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* National structure (Section 9) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 9" title={C.national_structure.heading} />
          <div className="mt-8 flex flex-col lg:flex-row items-stretch gap-2">
            {C.national_structure.chain.map((c, i) => (
              <React.Fragment key={c}>
                <div className="flex-1 rounded-xl border border-[#D7E1EF] bg-white p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">Layer {i + 1}</div>
                  <div className="mt-1 font-display font-semibold text-[#0B1220]">{c}</div>
                </div>
                {i < C.national_structure.chain.length - 1 && (
                  <div className="hidden lg:flex items-center text-[#062B5C]/50">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#0B1220] max-w-3xl">{C.national_structure.principle}</p>
        </Container>
      </section>

      {/* Terms (Section 10) */}
      <TwoColBlock
        kicker="Section 10"
        heading={C.terms.heading}
        body={C.terms.body}
        icon={<Vote className="h-4 w-4" />}
      />

      {/* Academy (Section 11) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 11" title={C.academy.heading} subtitle={C.academy.body} />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-6">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Curriculum</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {C.academy.curriculum.map((c) => (
                  <span key={c} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                    <GraduationCap className="h-3 w-3 mr-1.5" /> {c}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-lg bg-[#FFF7EE] border border-[#F47B20]/30 p-4 text-sm text-[#8A4B12] leading-relaxed">
                {C.academy.field_rule}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Candidate selection (Section 12) */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 12" title={C.candidate_selection.heading} subtitle={C.candidate_selection.intro} />
          <div className="mt-8">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Pipeline</div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {C.candidate_selection.pipeline.map((p, i) => (
                <React.Fragment key={p}>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-[#D7E1EF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#062B5C]">
                    <span className="h-4 w-4 rounded-full bg-[#062B5C] text-white text-[10px] flex items-center justify-center">{i + 1}</span>
                    {p}
                  </span>
                  {i < C.candidate_selection.pipeline.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Scoring framework</div>
            <div className="mt-3 space-y-2">
              {C.candidate_selection.scoring.map((s) => (
                <div key={s.criterion} className="grid grid-cols-1 sm:grid-cols-12 items-center gap-3 rounded-lg border border-[#D7E1EF] bg-white p-3">
                  <div className="sm:col-span-5 text-sm text-[#0B1220]">{s.criterion}</div>
                  <div className="sm:col-span-6">
                    <Progress value={s.weight * 4} className="h-2 bg-[#F5F8FC]" />
                  </div>
                  <div className="sm:col-span-1 text-right kpi-num text-sm font-semibold text-[#062B5C]">{s.weight}%</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#5B6B82]">{C.candidate_selection.note}</p>
          </div>
        </Container>
      </section>

      {/* Ethics (Section 13) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 13" title={C.ethics.heading} subtitle={C.ethics.body} />
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {C.ethics.process.map((p, i) => (
              <React.Fragment key={p}>
                <span className="inline-flex items-center rounded-md border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                  {p}
                </span>
                {i < C.ethics.process.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-5 text-sm text-[#5B6B82] max-w-3xl">{C.ethics.disclosures}</p>
        </Container>
      </section>

      {/* Finance (Section 14) */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Section 14" title={C.finance.heading} subtitle={C.finance.body} />
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {C.finance.chain.map((p, i) => (
              <React.Fragment key={p}>
                <span className="inline-flex items-center gap-1.5 rounded-md border border-[#D7E1EF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#062B5C]">
                  <Wallet className="h-3.5 w-3.5" /> {p}
                </span>
                {i < C.finance.chain.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-6 rounded-lg bg-[#EAF6EF] border border-[#138A4B]/30 p-4 text-sm text-[#0B1220]">
            {C.finance.note}
          </div>
        </Container>
      </section>

      {/* Internal democracy (Section 15) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 15" title={C.internal_democracy.heading} subtitle={C.internal_democracy.body} />
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {C.internal_democracy.sequence.map((p, i) => (
              <React.Fragment key={p}>
                <span className="inline-flex items-center rounded-md border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                  {p}
                </span>
                {i < C.internal_democracy.sequence.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-5 text-sm text-[#B54708] max-w-3xl">{C.internal_democracy.warning}</p>
        </Container>
      </section>

      {/* Membership + representation (Section 16-17) */}
      <section className="py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <SectionKicker>Section 16</SectionKicker>
              <h3 className="font-display mt-3 text-xl sm:text-2xl font-semibold text-[#0B1220]">{C.membership.heading}</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {C.membership.tiers.map((t) => (
                  <div key={t.name} className="rounded-xl border border-[#D7E1EF] bg-white p-4">
                    <div className="font-display font-semibold text-[#0B1220]">{t.name}</div>
                    <p className="mt-1 text-xs text-[#5B6B82] leading-relaxed">{t.body}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-[#5B6B82]">{C.membership.note}</p>
            </div>
            <div className="lg:col-span-5">
              <SectionKicker>Section 17</SectionKicker>
              <h3 className="font-display mt-3 text-xl sm:text-2xl font-semibold text-[#0B1220]">{C.representation.heading}</h3>
              <p className="mt-3 text-sm text-[#0B1220] leading-relaxed">{C.representation.body}</p>
            </div>
          </div>
        </Container>
      </section>

      {/* Technology (Section 18) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 18" title={C.technology.heading} subtitle={C.technology.body} />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {C.technology.modules.map((m) => (
              <div key={m} className="rounded-lg border border-[#D7E1EF] bg-white p-3 text-sm text-[#0B1220] flex items-center gap-2">
                <Cpu className="h-4 w-4 text-[#062B5C]" /> {m}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Performance review (Section 19) */}
      <TwoColBlock
        kicker="Section 19"
        heading={C.performance_review.heading}
        body={C.performance_review.body}
        icon={<Trophy className="h-4 w-4" />}
        chips={C.performance_review.factors}
      />

      {/* First target (Section 20) */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Section 20" title={C.first_target.heading} subtitle={C.first_target.intro} />
          <ol className="mt-8 space-y-3 max-w-3xl">
            {C.first_target.stages.map((s, i) => (
              <li key={s} className="flex items-start gap-3 rounded-lg border border-[#D7E1EF] bg-white p-4">
                <span className="h-7 w-7 rounded-md bg-[#062B5C] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm text-[#0B1220]">{s}</span>
              </li>
            ))}
          </ol>
          <p className="mt-6 font-display text-lg font-semibold text-[#062B5C]">{C.first_target.closing}</p>
        </Container>
      </section>

      {/* ECI Note */}
      <section className="py-14">
        <Container>
          <Card className="border-[#D7E1EF] bg-white" data-testid="constitution-eci-note">
            <CardContent className="p-6 sm:p-8 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#FFF1E8] border border-[#F47B20]/30 flex items-center justify-center text-[#F47B20] shrink-0">
                <Info className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Legal & Regulatory Note</div>
                <p className="mt-2 text-sm text-[#0B1220] leading-relaxed max-w-4xl">{C.eci_note}</p>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/dialogue">
              <Button className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="constitution-dialogue-cta">
                Share the reforms India needs
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/roadmap">
              <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="constitution-roadmap-cta">
                See the Launch Blueprint
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function TwoColBlock({ kicker, heading, body, icon, chips }) {
  return (
    <section className="py-16">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <SectionKicker>{kicker}</SectionKicker>
            <h3 className="font-display mt-3 text-xl sm:text-2xl font-semibold text-[#0B1220]">{heading}</h3>
          </div>
          <div className="lg:col-span-8">
            <Card className="border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <span className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C] shrink-0">
                    {icon}
                  </span>
                  <p className="text-sm text-[#0B1220] leading-relaxed">{body}</p>
                </div>
                {chips && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <span key={c} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Container>
    </section>
  );
}
